import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  Signal,
  signal,
  untracked,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  Assistant,
  AssistantKey,
  AssistantVersionKey,
  AssistantVersionSummary,
  ConfigVoiceConfig,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom, map, startWith } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';
import { AiAssistantService } from '../../../core/services/ai-assistant.service';
import { AvailableModelsService } from '../available-models.service';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { capitalizeFirst, clampIterations, formatVoiceConfig, LLMOptionPair } from '../test-run.utils';
import { AiVoiceConfigurationForm } from '../../../core/forms/ai-voice-model-config.form';
import { AiVoiceConfigurationFormComponent } from '../../ai-voice-configuration-form/ai-voice-configuration-form.component';

interface VoiceSummary {
  familyName: string;
  voiceName?: string;
}

type VoiceConfigMode = 'active' | 'override';

interface VoiceConfigEntry {
  form: AiVoiceConfigurationForm;
  summary: Signal<VoiceSummary>;
  valid: Signal<boolean>;
  mode: WritableSignal<VoiceConfigMode>;
}

interface RunPreview {
  model: string;
  modelName: string;
  reasoningEffort?: string;
  name: string;
  isEditing: boolean;
  voiceSummary?: VoiceSummary;
}

export interface TestRunOptionsModalResult {
  llmOptions: LLMOptionPair[];
  /** An undefined entry uses the assistant's own voice settings. */
  voiceConfigs: (ConfigVoiceConfig | undefined)[];
  iterations: number;
  runNames: string[];
  /** Left unset for the current configuration, which is what the assistant runs today. */
  assistantVersionKey?: Partial<AssistantVersionKey>;
}

export interface TestRunOptionsModalData {
  assistant?: Assistant;
  voiceOnly?: boolean;
}

interface VersionOption {
  id: string;
  label: string;
  /** Unset only when the assistant has no published version to name. */
  versionKey?: Partial<AssistantVersionKey>;
}

/** Matches the version history panel, which is where the same list is offered in the editor. */
const VERSION_PAGE_SIZE = 25;
const CURRENT_VERSION_ID = '';

interface ModelSelection {
  id: string;
  name: string;
  isDefault: boolean;
  supportedReasoningEfforts: string[];
  selected: boolean;
  // Changed from single string to array to support multiple selections
  selectedReasoningEfforts: string[];
}

@Component({
  selector: 'ai-test-run-options-modal',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    GalaxyBadgeModule,
    GalaxyButtonGroupModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    GalaxyTooltip,
    AiAssistantI18nModule,
    AiVoiceConfigurationFormComponent,
  ],

  templateUrl: './test-run-options-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './test-run-options-modal.component.scss',
})
export class TestRunOptionsModalComponent {
  private readonly dialogRef = inject(MatDialogRef<TestRunOptionsModalComponent, TestRunOptionsModalResult>);
  private readonly availableModelsService = inject(AvailableModelsService);
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly translateService = inject(TranslateService);
  protected readonly data = inject<TestRunOptionsModalData>(MAT_DIALOG_DATA, { optional: true });
  readonly voiceOnly = this.data?.voiceOnly ?? false;
  private readonly activeVoiceConfig = signal<ConfigVoiceConfig | undefined>(this.data?.assistant?.config?.voiceConfig);
  readonly activeVoiceLabel = computed(() => formatVoiceConfig(this.activeVoiceConfig()));
  readonly activeVoiceLoading = signal(false);
  readonly activeVoiceLoadFailed = signal(false);
  private activeVoiceRequest = 0;

  private get assistantDefaultModel(): string | undefined {
    return this.data?.assistant?.config?.models?.[0]?.id;
  }

  // Signal to track model selections
  modelSelections = signal<ModelSelection[]>([]);

  iterations = signal<number>(5);

  private readonly rawModels = toSignal(
    this.availableModelsService.getModels(this.data?.assistant?.type).pipe(
      map((modelsList) => {
        let assistantModelId = this.assistantDefaultModel;

        // If no assistantModelId, use the recommended model (this is the model the assistant uses if none is selected)
        if (!assistantModelId) {
          const recommendedModel = modelsList.find((model) => model.recommended);
          if (recommendedModel?.id) {
            assistantModelId = recommendedModel.id;
          }
        }

        const selections: ModelSelection[] = modelsList.map((model) => {
          const modelId = model.id || '';
          const isDefault = !!(assistantModelId && modelId === assistantModelId);
          const supportedEfforts = model.supportedReasoningEfforts || [];

          return {
            id: modelId,
            name: model.name || modelId,
            isDefault,
            supportedReasoningEfforts: supportedEfforts,
            // Pre-select the default model
            selected: isDefault,
            // Pre-select the first reasoning effort if model supports it
            selectedReasoningEfforts: supportedEfforts.length > 0 ? [supportedEfforts[0]] : [],
          };
        });

        return selections;
      }),
    ),
  );

  readonly voiceConfigEntries = signal<VoiceConfigEntry[]>([]);
  private readonly injector = inject(Injector);

  // Assistant-scoped runs only: a suite spans assistants and so has no single version to run against.
  readonly showVersionSelector = !!this.data?.assistant?.id;
  readonly versionOptions = signal<VersionOption[]>([]);
  readonly versionsLoading = signal(false);
  readonly versionLoadFailed = signal(false);
  readonly draftCheckFailed = signal(false);
  readonly selectedVersionId = signal<string>(CURRENT_VERSION_ID);

  readonly selectedVersionOption = computed(() =>
    this.versionOptions().find((option) => option.id === this.selectedVersionId()),
  );

  constructor() {
    effect(() => {
      const models = this.rawModels();
      if (models && models.length > 0) {
        untracked(() => {
          this.modelSelections.set(models);
          this.buildRunPreviews();
        });
      }
    });

    effect(() => {
      if (!this.voiceOnly) return;
      const entries = this.voiceConfigEntries();
      const contextName =
        this.data?.assistant?.name ||
        this.translateService.instant('AI_ASSISTANT.TESTING.RUN_OPTIONS.RUN_NAME_FALLBACK');
      const activeSuffix = this.translateService.instant('AI_ASSISTANT.TESTING.RUN_OPTIONS.ACTIVE_VOICE_SUFFIX');
      const voiceFallback = this.translateService.instant('AI_ASSISTANT.TESTING.RUN_OPTIONS.VOICE_FAMILY_FALLBACK');
      // untracked: a version change re-suffixes the previews in place, so re-running here would only serve to
      // throw away names the user has typed.
      const versionSuffix = untracked(() => this.versionNameSuffix());
      const previews: RunPreview[] = entries.map((entry) => {
        if (entry.mode() === 'active') {
          return {
            model: '',
            modelName: '',
            name: `${contextName} - ${activeSuffix}${versionSuffix}`,
            isEditing: false,
          };
        }
        const summary = entry.summary();
        const voiceLabel = summary.voiceName
          ? `${summary.familyName}/${summary.voiceName}`
          : summary.familyName || voiceFallback;
        return {
          model: '',
          modelName: '',
          name: `${contextName} - ${voiceLabel}${versionSuffix}`,
          isEditing: false,
          voiceSummary: summary,
        };
      });
      untracked(() => this.runPreviews.set(previews));
    });

    if (this.voiceOnly) {
      this.voiceConfigEntries.set([this.createVoiceEntry(this.activeVoiceConfig(), 'active')]);
    }

    if (this.showVersionSelector) {
      void this.loadVersionOptions();
    }
  }

  selectVersion(id: string): void {
    const previousSuffix = this.versionNameSuffix();
    this.selectedVersionId.set(id);
    this.reSuffixRunPreviews(previousSuffix, this.versionNameSuffix());
    if (this.voiceOnly) {
      void this.loadActiveVoiceConfig();
    }
  }

  // "Use assistant settings" has to mean the settings of the version being run, so the entries left on active
  // follow the selection instead of pinning whatever the assistant happens to be configured with today.
  private async loadActiveVoiceConfig(): Promise<void> {
    const request = ++this.activeVoiceRequest;
    const versionKey = this.selectedVersionOption()?.versionKey;
    let voiceConfig = this.data?.assistant?.config?.voiceConfig;
    let failed = false;

    if (this.selectedVersionId() !== CURRENT_VERSION_ID && versionKey) {
      this.activeVoiceLoading.set(true);
      try {
        const version = await firstValueFrom(this.aiAssistantService.getAssistantVersion(versionKey));
        // A version that resolves without a voice config — one published before the assistant took calls, or a
        // degraded read — would otherwise pass the current voice off as that version's. The voice service takes
        // an explicit config over the pinned version's own, so this cannot be left to resolve server-side.
        const versionVoice = version?.config?.voiceConfig;
        failed = !versionVoice;
        voiceConfig = versionVoice ?? voiceConfig;
      } catch (err) {
        console.error('Error loading the voice settings of the assistant version to test', versionKey.version, err);
        failed = true;
      }
    }

    // Counted rather than compared against the selection, so a read that resolves out of order neither clobbers
    // a newer one's config nor clears the loading it is still holding. The newest read always settles all three.
    if (request !== this.activeVoiceRequest) {
      return;
    }
    this.activeVoiceLoading.set(false);
    this.activeVoiceLoadFailed.set(failed);
    this.activeVoiceConfig.set(voiceConfig);
  }

  // Swaps the version suffix on the existing previews rather than rebuilding them, so a name the user typed
  // survives changing the version.
  private reSuffixRunPreviews(previousSuffix: string, nextSuffix: string): void {
    if (previousSuffix === nextSuffix) {
      return;
    }
    this.runPreviews.update((previews) =>
      previews.map((preview) => {
        const base =
          previousSuffix && preview.name.endsWith(previousSuffix)
            ? preview.name.slice(0, -previousSuffix.length)
            : preview.name;
        // A typed name may already end with the label being applied, in which case appending would double it.
        return { ...preview, name: base.endsWith(nextSuffix) ? base : `${base}${nextSuffix}` };
      }),
    );
  }

  // Settled separately so one failed read still leaves the other's versions selectable, and so the two can say
  // different things — a failed draft read is not "there is no draft".
  private async loadVersionOptions(): Promise<void> {
    const assistant = this.data?.assistant;
    const key: Partial<AssistantKey> = { id: assistant?.id, namespace: assistant?.namespace };
    this.versionsLoading.set(true);
    const [draft, published] = await Promise.allSettled([
      firstValueFrom(this.aiAssistantService.getCurrentDraft(key)),
      firstValueFrom(this.aiAssistantService.listPublishedAssistantVersions(key, { pageSize: VERSION_PAGE_SIZE })),
    ]);

    if (draft.status === 'rejected') {
      console.error('Error checking for an assistant draft to test', assistant?.id, draft.reason);
      this.draftCheckFailed.set(true);
    }
    if (published.status === 'rejected') {
      console.error('Error loading assistant versions to test', assistant?.id, published.reason);
      this.versionLoadFailed.set(true);
    }

    this.versionOptions.set(
      this.buildVersionOptions(
        draft.status === 'fulfilled' ? draft.value : null,
        published.status === 'fulfilled' ? published.value.versions : [],
      ),
    );
    this.selectedVersionId.set(CURRENT_VERSION_ID);
    this.versionsLoading.set(false);
  }

  private buildVersionOptions(
    draft: AssistantVersionSummary | null,
    published: AssistantVersionSummary[],
  ): VersionOption[] {
    const options: VersionOption[] = [];

    if (draft?.key?.version) {
      options.push({
        id: draft.key.version,
        label: this.translateService.instant('AI_ASSISTANT.SETTINGS.VERSION_HISTORY.DRAFT.ROW_TITLE'),
        versionKey: draft.key,
      });
    }

    published.forEach((version, index) => {
      const number = version.key?.versionNumber;
      // The newest published version is the current configuration, so it is the default selection — but the run
      // still names it, so the version a run exercised is recorded rather than inferred from "not set".
      if (index === 0) {
        options.push({
          id: CURRENT_VERSION_ID,
          label: this.currentVersionLabel(number),
          versionKey: version.key?.version ? version.key : undefined,
        });
        return;
      }
      if (!version.key?.version) {
        return;
      }
      options.push({
        id: version.key.version,
        label: this.translateService.instant('AI_ASSISTANT.SETTINGS.VERSION_HISTORY.VERSION_NUMBER', { number }),
        versionKey: version.key,
      });
    });

    if (!options.some((option) => option.id === CURRENT_VERSION_ID)) {
      options.push({ id: CURRENT_VERSION_ID, label: this.currentVersionLabel(undefined) });
    }

    return options;
  }

  private currentVersionLabel(versionNumber: number | undefined): string {
    return versionNumber
      ? this.translateService.instant('AI_ASSISTANT.TESTING.RUN_OPTIONS.VERSION_CURRENT_NUMBERED', {
          number: versionNumber,
        })
      : this.translateService.instant('AI_ASSISTANT.TESTING.RUN_OPTIONS.VERSION_CURRENT');
  }

  // Keyed on the option, not on whether it carries a version key: the current configuration now sends its key
  // too, and naming every default run after the version it already runs against tells the user nothing.
  private versionNameSuffix(): string {
    const option = this.selectedVersionOption();
    return option && option.id !== CURRENT_VERSION_ID ? ` - ${option.label}` : '';
  }

  addVoiceConfig(): void {
    this.voiceConfigEntries.update((entries) => [
      ...entries,
      this.createVoiceEntry(this.activeVoiceConfig(), 'override'),
    ]);
  }

  removeVoiceConfig(form: AiVoiceConfigurationForm): void {
    this.voiceConfigEntries.update((entries) =>
      entries.length > 1 ? entries.filter((e) => e.form !== form) : entries,
    );
  }

  setEntryMode(entry: VoiceConfigEntry, mode: VoiceConfigMode): void {
    entry.mode.set(mode);
  }

  private createVoiceEntry(
    seed: ConfigVoiceConfig | undefined,
    mode: VoiceConfigMode,
  ): VoiceConfigEntry {
    const form = new AiVoiceConfigurationForm(seed);
    const initial: VoiceSummary = { familyName: '', voiceName: undefined };
    const summary = toSignal(form.voiceSummary$.pipe(map((s): VoiceSummary => s ?? initial)), {
      initialValue: initial,
      injector: this.injector,
    });
    const valid = toSignal(form.statusChanges.pipe(startWith(form.status), map(() => form.valid)), {
      initialValue: form.valid,
      injector: this.injector,
    });
    return { form, summary, valid, mode: signal(mode) };
  }

  readonly voiceConfigsValid = computed(() =>
    this.voiceConfigEntries().every((e) => e.mode() === 'active' || e.valid()),
  );

  readonly modelsLoading = computed(() => this.rawModels() === undefined);

  readonly hasSelection = computed(() => {
    return this.modelSelections().some((m) => m.selected);
  });

  /**
   * Count of total test configurations (model + reasoning effort combinations)
   */
  readonly selectedCount = computed(() => {
    let count = 0;
    for (const model of this.modelSelections()) {
      if (!model.selected) continue;

      if (model.supportedReasoningEfforts.length > 0) {
        // Count each selected reasoning effort as a separate configuration
        count += model.selectedReasoningEfforts.length;
      } else {
        // Models without reasoning count as 1
        count += 1;
      }
    }
    return count;
  });

  runPreviews = signal<RunPreview[]>([]);

  private buildRunPreviews(): void {
    if (this.voiceOnly) return;
    const contextName =
      this.data?.assistant?.name ||
      this.translateService.instant('AI_ASSISTANT.TESTING.RUN_OPTIONS.RUN_NAME_FALLBACK');
    const versionSuffix = this.versionNameSuffix();
    const previews: RunPreview[] = [];

    for (const model of this.modelSelections()) {
      if (!model.selected) continue;

      if (model.supportedReasoningEfforts.length > 0) {
        for (const effort of model.selectedReasoningEfforts) {
          previews.push({
            model: model.id,
            modelName: model.name,
            reasoningEffort: effort,
            name: `${contextName} - ${model.name} (${capitalizeFirst(effort)})${versionSuffix}`,
            isEditing: false,
          });
        }
      } else {
        previews.push({
          model: model.id,
          modelName: model.name,
          name: `${contextName} - ${model.name}${versionSuffix}`,
          isEditing: false,
        });
      }
    }

    this.runPreviews.set(previews);
  }

  updatePreviewName(preview: RunPreview, name: string): void {
    const previews = this.runPreviews();
    this.runPreviews.set(previews.map((p) => (p === preview ? { ...p, name } : p)));
  }

  toggleEditName(preview: RunPreview): void {
    const previews = this.runPreviews();
    this.runPreviews.set(previews.map((p) => (p === preview ? { ...p, isEditing: !p.isEditing } : p)));
  }

  toggleModel(model: ModelSelection): void {
    const selections = this.modelSelections();
    const updated = selections.map((m) => {
      if (m.id === model.id) {
        const newSelected = !m.selected;
        return {
          ...m,
          selected: newSelected,
          // When selecting a model with reasoning support, auto-select first effort if none selected
          selectedReasoningEfforts:
            newSelected && m.supportedReasoningEfforts.length > 0 && m.selectedReasoningEfforts.length === 0
              ? [m.supportedReasoningEfforts[0]]
              : m.selectedReasoningEfforts,
        };
      }
      return m;
    });
    this.modelSelections.set(updated);
    this.buildRunPreviews();
  }

  toggleReasoningEffort(model: ModelSelection, effort: string): void {
    const selections = this.modelSelections();
    const updated = selections.map((m) => {
      if (m.id === model.id) {
        const currentEfforts = m.selectedReasoningEfforts;
        const isSelected = currentEfforts.includes(effort);

        let newEfforts: string[];
        if (isSelected) {
          // Don't allow deselecting the last effort
          if (currentEfforts.length === 1) {
            return m;
          }
          newEfforts = currentEfforts.filter((e) => e !== effort);
        } else {
          newEfforts = [...currentEfforts, effort];
        }

        return { ...m, selectedReasoningEfforts: newEfforts };
      }
      return m;
    });
    this.modelSelections.set(updated);
    this.buildRunPreviews();
  }

  /**
   * Check if a reasoning effort is selected for a model
   */
  isReasoningEffortSelected(model: ModelSelection, effort: string): boolean {
    return model.selectedReasoningEfforts.includes(effort);
  }

  protected capitalizeFirst = capitalizeFirst;

  updateIterations(event: Event): void {
    const value = (event.target as HTMLInputElement).valueAsNumber;
    this.iterations.set(clampIterations(value));
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.voiceOnly) {
      this.dialogRef.close({
        llmOptions: [],
        // Sending nothing uses the assistant's own voice.
        voiceConfigs: this.voiceConfigEntries().map((e) => {
          if (e.mode() !== 'active') return e.form.toAssistantVoiceConfig();
          return this.selectedVersionId() === CURRENT_VERSION_ID
            ? undefined
            : new AiVoiceConfigurationForm(this.activeVoiceConfig()).toAssistantVoiceConfig();
        }),
        iterations: this.iterations(),
        runNames: this.runPreviews().map((p) => p.name),
        assistantVersionKey: this.selectedVersionOption()?.versionKey,
      });
      return;
    }

    const llmOptions: LLMOptionPair[] = [];

    for (const model of this.modelSelections()) {
      if (!model.selected) continue;

      if (model.supportedReasoningEfforts.length > 0) {
        for (const effort of model.selectedReasoningEfforts) {
          llmOptions.push({
            model: model.id || undefined,
            reasoningEffort: effort,
          });
        }
      } else {
        llmOptions.push({
          model: model.id || undefined,
          reasoningEffort: undefined,
        });
      }
    }

    const result: TestRunOptionsModalResult = {
      llmOptions,
      voiceConfigs: [],
      iterations: this.iterations(),
      runNames: this.runPreviews().map((p) => p.name),
      assistantVersionKey: this.selectedVersionOption()?.versionKey,
    };

    this.dialogRef.close(result);
  }
}
